import { TypedDocumentNode, gql } from '@apollo/client';
import { generatePath, useNavigate } from 'react-router-dom';

import { SeasonLeaderboardList as CoreSeasonLeaderboardList } from '@sorare/core/src/components/rivals/Seasons/SeasonLeaderboardList';
import Avatar from '@sorare/core/src/components/user/Avatar';
import { FOOTBALL_PLAY_RIVALS_LEADERBOARD_SEASONSLUG_USERSLUG_SELECTEDUSERSLUG_ARENA } from '@sorare/core/src/constants/__generated__/routes';

import { SeasonLeaderboardList_footballRivalsDivisionRanking } from './__generated__/index.graphql';

const addAvatarToRanking = (
  ranking: SeasonLeaderboardList_footballRivalsDivisionRanking
) => {
  return {
    ...ranking,
    avatar: <Avatar user={ranking.manager.user} size={4} rounded />,
  };
};

type Props = {
  upperDivisionTitle?: string;
  lowerDivisionTitle?: string;
  promotedRankings: SeasonLeaderboardList_footballRivalsDivisionRanking[];
  unrelegatedRankings: SeasonLeaderboardList_footballRivalsDivisionRanking[];
  relegatedRankings: SeasonLeaderboardList_footballRivalsDivisionRanking[];
  featuredManagerId?: string;
  seasonSlug?: string;
  userSlug?: string;
  selectedUserSlug?: string;
};
export const SeasonLeaderboardList = ({
  upperDivisionTitle,
  lowerDivisionTitle,
  promotedRankings,
  unrelegatedRankings,
  relegatedRankings,
  featuredManagerId,
  seasonSlug,
  userSlug,
  selectedUserSlug,
}: Props) => {
  const navigate = useNavigate();

  const onRowClick = (newSelectedUserSlug: string) => {
    navigate(
      generatePath(
        FOOTBALL_PLAY_RIVALS_LEADERBOARD_SEASONSLUG_USERSLUG_SELECTEDUSERSLUG_ARENA,
        {
          seasonSlug: seasonSlug || '',
          userSlug: userSlug || '',
          selectedUserSlug: newSelectedUserSlug,
        }
      ),
      { replace: true }
    );
  };
  return (
    <CoreSeasonLeaderboardList
      featuredManagerId={featuredManagerId}
      promotedRankings={promotedRankings.map(addAvatarToRanking)}
      unrelegatedRankings={unrelegatedRankings.map(addAvatarToRanking)}
      relegatedRankings={relegatedRankings.map(addAvatarToRanking)}
      upperDivisionTitle={upperDivisionTitle}
      lowerDivisionTitle={lowerDivisionTitle}
      selectedUserSlug={selectedUserSlug}
      onRowClick={onRowClick}
    />
  );
};

SeasonLeaderboardList.fragments = {
  footballRivalsDivisionRanking: gql`
    fragment SeasonLeaderboardList_footballRivalsDivisionRanking on FootballRivalsDivisionRanking {
      id
      cumulatedScoreDifference
      points
      ranking
      manager {
        id
        user {
          slug
          nickname
          ...Avatar_user
        }
      }
      liveGamesCount
      upcomingGamesCount
      eligibleReward {
        probabilisticBundleConfiguration {
          id
          pictureUrl
        }
      }
    }
    ${Avatar.fragments.user}
  ` as TypedDocumentNode<SeasonLeaderboardList_footballRivalsDivisionRanking>,
};
